import { useCallback, useEffect, useRef, useState } from "react";

const STORAGE_PREFIX = "wg.simulations.collapsedGroups:";

const readKeys = (storageKey) => {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(storageKey));
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((key) => typeof key === "string"));
  } catch {
    return new Set();
  }
};

const writeKeys = (storageKey, keys) => {
  try {
    sessionStorage.setItem(storageKey, JSON.stringify([...keys]));
  } catch {
    // Storage full or blocked: the groups stay collapsed until a remount.
  }
};

/**
 * The Simulations list's collapsed group keys ("day-2026-10-13", ...), kept
 * in sessionStorage per microgrid so a collapsed group stays collapsed after
 * the list refetches (the page swaps the Table for a spinner), after "Back to
 * Simulations" remounts the page with a new run, and after a browser refresh.
 * They are forgotten when the tab closes.
 *
 * The keys are read once, at mount; the page remounts when the microgrid
 * changes. Nothing is pruned: a key whose group has aged into a coarser tier
 * just stops matching, and the list dies with the tab.
 *
 * @param {string} scope - "<storage account>/<microgrid>"
 * @returns {[Set<string>, (groupKey: string, collapsed: boolean) => void]}
 */
export default function useCollapsedGroups(scope) {
  const storageKey = `${STORAGE_PREFIX}${scope}`;
  const [collapsed, setCollapsedKeys] = useState(() => readKeys(storageKey));
  const initial = useRef(collapsed);

  useEffect(() => {
    // Only write a change, so opening a microgrid stores nothing.
    if (collapsed === initial.current) return;
    writeKeys(storageKey, collapsed);
  }, [storageKey, collapsed]);

  const setCollapsed = useCallback((groupKey, isCollapsed) => {
    setCollapsedKeys((prev) => {
      if (prev.has(groupKey) === isCollapsed) return prev;
      const next = new Set(prev);
      if (isCollapsed) next.add(groupKey);
      else next.delete(groupKey);
      return next;
    });
  }, []);

  return [collapsed, setCollapsed];
}
